import { Phone, Calendar, Wrench, CheckCircle, LucideIcon } from 'lucide-react';

interface Step {
  number: number;
  icon: LucideIcon;
  title: string;
  timing: string;
  description: string;
}

interface WhatHappensNextProps {
  variant?: 'light' | 'dark';
}

const steps: Step[] = [
  {
    number: 1,
    icon: Phone,
    title: "We Call You Back",
    timing: "After you reach out",
    description: "We talk through your building, the work you need and your timeline"
  },
  {
    number: 2,
    icon: Calendar,
    title: "Walk the Site",
    timing: "At a time that works for you",
    description: "We look at the space so the quote matches the real job"
  },
  {
    number: 3,
    icon: Wrench,
    title: "Get a Written Proposal",
    timing: "When you're ready",
    description: "A clear scope of work, then our own crew does the install"
  },
  {
    number: 4,
    icon: CheckCircle,
    title: "Tested and Handed Over",
    timing: "At handover",
    description: "Every cable run is tested before handover, and you have one local team to call"
  }
];

export function WhatHappensNext({ variant = 'light' }: WhatHappensNextProps) {
  const isDark = variant === 'dark';

  return (
    <section className={isDark
      ? "py-16 bg-navy-900 border-t border-white/5"
      : "py-16 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
    }>
      <div className="container mx-auto px-4">
        <h2 className={`text-3xl font-bold text-center mb-12 ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
          What Happens Next?
        </h2>

        <div className="grid md:grid-cols-4 grid-cols-2 gap-6 max-w-5xl mx-auto">
          {steps.map((step) => {
            const IconComponent = step.icon;
            return (
              <div
                key={step.number}
                className={isDark
                  ? "bg-white/5 border border-white/10 rounded-xl p-6 relative"
                  : "bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md relative"
                }
              >
                {/* Step number badge */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-cobalt-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                  {step.number}
                </div>

                {/* Icon container */}
                <div className={isDark
                  ? "w-16 h-16 bg-cobalt-500/10 rounded-xl flex items-center justify-center mb-4 mx-auto"
                  : "w-16 h-16 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex items-center justify-center mb-4 mx-auto"
                }>
                  <IconComponent className={isDark
                    ? "w-8 h-8 text-cobalt-400"
                    : "w-8 h-8 text-blue-600 dark:text-blue-400"
                  } />
                </div>

                {/* Title */}
                <h3 className={`font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {step.title}
                </h3>

                {/* Timing badge */}
                <div className="flex justify-center mb-3">
                  <span className={isDark
                    ? "bg-cobalt-500/10 text-cobalt-300 px-3 py-1 rounded-full text-sm font-medium"
                    : "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium"
                  }>
                    {step.timing}
                  </span>
                </div>

                {/* Description */}
                <p className={`text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
